<template>
  <div class="invoice-issue-container">
    <div class="invoice-issue-header">
      <span class="back" @click="$router.push('/invoice/unissued')">&#8592;</span>
      <span class="title">开具发票</span>
    </div>
    
    <!-- 发票抬头卡片 -->
    <div class="invoice-issue-card">
      <div class="invoice-issue-card-label">发票抬头</div>
      <div class="invoice-issue-card-row">
        <span class="invoice-issue-card-value">{{ selectedHead?.companyName || selectedHead?.personalName || '请选择发票抬头' }}</span>
        <span class="invoice-issue-card-link" @click="goToTitleManage">更多/添加</span>
      </div>
      <div class="invoice-issue-card-row">
        <span class="invoice-issue-card-label-small">抬头类型</span>
        <span class="invoice-issue-card-value-small">{{ headTypeText }}</span>
      </div>
      <div class="invoice-issue-card-row">
        <span class="invoice-issue-card-label-small">税号</span>
        <span class="invoice-issue-card-value-small">{{ selectedHead?.taxId || selectedHead?.taxNumber || '--' }}</span>
      </div>
    </div>
    
    <!-- 总金额 -->
    <div class="invoice-issue-card">
      <div class="invoice-issue-amount-row">
        <span class="invoice-issue-amount-label">总金额</span>
        <span class="invoice-issue-amount-value">{{ formatAmount(totalAmount) }}元</span>
      </div>
    </div>
    
    <!-- 发票须知 -->
    <div class="invoice-issue-card">
      <div class="invoice-issue-notice">发票将在1-3个工作日内开具</div>
      <div class="invoice-issue-notice-link" @click="showNotice">查看发票须知</div>
    </div>
    
    <!-- 提交按钮 -->
    <div class="invoice-issue-actions">
      <button class="invoice-issue-btn" @click="submitInvoice" :disabled="!canSubmit">提交申请</button>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  name: 'InvoiceIssue',
  data () {
    return {
      selectedHead: null,
      totalAmount: 0,
      paymentIds: [],
      orderType: 'MERCHANTRE_CHARGE_FEE' // 订单类型，商户端固定为商户充值
    }
  },
  computed: {
    headTypeText () {
      if (!this.selectedHead) return '--'
      if (this.selectedHead.invoiceHeadType === 'ENTERPRISE') {
        return '非自然人'
      }
      return '个人/事业单位'
    },
    canSubmit () {
      return this.selectedHead && this.paymentIds.length > 0 && this.totalAmount > 0
    }
  },
  async mounted () {
    // 从路由参数获取支付记录信息
    const { paymentIds, totalAmount, selectedHeadId, orderType } = this.$route.query
    
    // 优先从路由参数获取，如果没有则从 sessionStorage 恢复
    if (paymentIds) {
      this.paymentIds = paymentIds.split(',').filter(id => id)
      // 保存到 sessionStorage
      sessionStorage.setItem('invoiceIssue_paymentIds', paymentIds)
    } else {
      // 从 sessionStorage 恢复
      const savedPaymentIds = sessionStorage.getItem('invoiceIssue_paymentIds')
      if (savedPaymentIds) {
        this.paymentIds = savedPaymentIds.split(',').filter(id => id)
      }
    }
    
    if (totalAmount) {
      this.totalAmount = parseInt(totalAmount) || 0
      // 保存到 sessionStorage
      sessionStorage.setItem('invoiceIssue_totalAmount', String(this.totalAmount))
    } else {
      // 从 sessionStorage 恢复
      const savedTotalAmount = sessionStorage.getItem('invoiceIssue_totalAmount')
      if (savedTotalAmount) {
        this.totalAmount = parseInt(savedTotalAmount) || 0
      }
    }
    
    // 获取订单类型
    if (orderType) {
      this.orderType = orderType
      sessionStorage.setItem('invoiceIssue_orderType', orderType)
    } else {
      // 从 sessionStorage 恢复
      const savedOrderType = sessionStorage.getItem('invoiceIssue_orderType')
      if (savedOrderType) {
        this.orderType = savedOrderType
      }
    }
    
    // 加载发票抬头（优先使用选中的，否则使用默认的）
    if (selectedHeadId) {
      await this.loadHeadById(selectedHeadId)
    } else {
      await this.loadDefaultHead()
    }
  },
  methods: {
    async loadDefaultHead () {
      try {
        // 获取发票抬头列表，选择默认的
        // 微信小程序中使用 query 参数：?pageNo=${page}&pageSize=${pageSize}
        const response = await request({
          url: '/invoice/invoiceHead/search?pageNo=1&pageSize=10',
          method: 'post',
          data: {}, // POST body 为空对象
          timeout: 30000
        })
        
        // request 拦截器返回的是 response.data，即 { code: 200, data: [...] }
        // 微信小程序返回格式：res.data.data
        let heads = []
        if (response?.code === 200 && response?.data) {
          heads = Array.isArray(response.data) ? response.data : []
        }
        // 查找默认抬头
        const defaultHead = heads.find(h => h.isDefault === true || h.isDefault === 1) || heads[0]
        if (defaultHead) {
          this.selectedHead = defaultHead
        }
      } catch (e) {
        console.error('加载发票抬头失败:', e)
        // 静默失败，不影响页面显示
      }
    },
    async loadHeadById (headId) {
      try {
        // 微信小程序中使用 query 参数：?id=${id}
        const response = await request({
          url: `/invoice/invoiceHead/getById?id=${headId}`,
          method: 'post',
          data: {}, // POST body 为空对象
          timeout: 30000
        })
        
        // request 拦截器返回的是 response.data，即 { code: 200, data: {...} }
        // 微信小程序返回格式：res.data.data
        if (response?.code === 200 && response?.data) {
          if (typeof response.data === 'object' && !Array.isArray(response.data)) {
            this.selectedHead = response.data
          }
        }
      } catch (e) {
        console.error('加载发票抬头失败:', e)
        // 如果加载失败，尝试加载默认抬头
        await this.loadDefaultHead()
      }
    },
    goToTitleManage () {
      // 保存当前金额和支付记录ID到 sessionStorage，防止路由参数丢失
      if (this.totalAmount) {
        sessionStorage.setItem('invoiceIssue_totalAmount', String(this.totalAmount))
      }
      if (this.paymentIds && this.paymentIds.length > 0) {
        sessionStorage.setItem('invoiceIssue_paymentIds', this.paymentIds.join(','))
      }
      if (this.orderType) {
        sessionStorage.setItem('invoiceIssue_orderType', this.orderType)
      }
      
      // 跳转到发票抬头管理页面，传递当前选中的抬头和金额信息
      this.$router.push({
        path: '/invoice/title',
        query: {
          returnPath: '/invoice/issue',
          selectedHeadId: this.selectedHead?.id,
          // 保留金额和支付记录ID，以便返回时恢复
          totalAmount: this.totalAmount,
          paymentIds: this.paymentIds.join(','),
          orderType: this.orderType
        }
      })
    },
    formatAmount (amount) {
      if (!amount && amount !== 0) return '0.00'
      // 金额单位是分，需要转换为元
      const yuan = (amount / 100).toFixed(2)
      return yuan
    },
    showNotice () {
      alert('发票须知：\n1. 发票将在1-3个工作日内开具\n2. 电子发票将发送至指定邮箱\n3. 发票一旦开具，无法修改')
    },
    async submitInvoice () {
      if (!this.canSubmit) {
        alert('请完善开票信息')
        return
      }
      
      try {
        // 获取邮箱（从抬头信息中获取，如果没有则需要用户输入）
        let email = this.selectedHead?.contactEmail || this.selectedHead?.email
        if (!email) {
          const inputEmail = prompt('请输入接收发票的邮箱地址：')
          if (!inputEmail) {
            return
          }
          // 这里可以验证邮箱格式
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputEmail)) {
            alert('请输入有效的邮箱地址')
            return
          }
          email = inputEmail
        }
        
        // 提交开票申请
        // 确保 amount 是整数（分），paymentIds 是数组
        // totalAmount 已经是分单位（从后端返回的 amount 累加得到），直接使用
        const amountFen = Math.round(this.totalAmount) // 确保是整数，单位是分
        const response = await request({
          url: '/invoice/invoiceSubmit',
          method: 'post',
          data: {
            paymentIds: this.paymentIds, // 数组格式
            orderType: this.orderType, // 订单类型，从路由参数或 sessionStorage 获取
            invoiceHeadId: this.selectedHead.id,
            amount: amountFen, // 整数，单位是分
            email: email,
            invoiceType: 'NORMAL', // 普通发票，如果需要增值税专用发票可以添加选择
            showPlateNumber: false // 商户端固定为 false，不在发票备注中展示车牌号
            // 注意：后端 InvoiceSubmitParams 中没有 remark 字段，所以不传递
          },
          timeout: 30000
        })
        
        // request 拦截器已经处理了 response.data，这里直接判断 code
        if (response?.code === 200 || response?.code === 2101) {
          // 清理 sessionStorage
          sessionStorage.removeItem('invoiceIssue_totalAmount')
          sessionStorage.removeItem('invoiceIssue_paymentIds')
          sessionStorage.removeItem('invoiceIssue_orderType')
          
          alert('开票申请已提交，发票将在1-3个工作日内开具')
          // 跳转到已开票页面
          this.$router.push('/invoice/unissued?tab=issued')
        } else {
          const errorMsg = response?.message || response?.msg || '提交失败，请重试'
          // 检查是否是"不可重复开票"错误
          if (response?.code === 2100 || errorMsg.includes('不可重复开票') || errorMsg.includes('重复开票')) {
            alert('该订单已提交开票申请')
          } else {
            alert(errorMsg)
          }
        }
      } catch (e) {
        console.error('提交开票申请失败:', e)
        // request.js 会把 code != 200/0 的业务码抛异常，这里需要兼容发票服务的业务码
        const respData = e?.response?.data
        const code = e?.code ?? respData?.code
        const errorMsg = respData?.message || respData?.msg || e?.message || '提交失败，请重试'
        // 2101：后端可能用来表示“已受理/已提交”（页面原逻辑当成功）
        if (code === 2101) {
          sessionStorage.removeItem('invoiceIssue_totalAmount')
          sessionStorage.removeItem('invoiceIssue_paymentIds')
          sessionStorage.removeItem('invoiceIssue_orderType')
          alert('开票申请已提交，发票将在1-3个工作日内开具')
          this.$router.push('/invoice/unissued?tab=issued')
          return
        }
        // 2100：不可重复开票（页面原逻辑当已提交）
        if (code === 2100 || (typeof errorMsg === 'string' && (errorMsg.includes('不可重复开票') || errorMsg.includes('重复开票')))) {
          alert('该订单已提交开票申请')
          return
        }
        alert(errorMsg)
      }
    }
  },
  watch: {
    '$route.query' (newQuery) {
      // 当路由参数变化时（比如从抬头选择页面返回），重新读取所有参数
      if (newQuery.selectedHeadId) {
        this.loadHeadById(newQuery.selectedHeadId)
      }
      
      // 重新读取金额和支付记录ID，优先从路由参数，如果没有则从 sessionStorage 恢复
      if (newQuery.totalAmount) {
        this.totalAmount = parseInt(newQuery.totalAmount) || 0
        sessionStorage.setItem('invoiceIssue_totalAmount', String(this.totalAmount))
      } else {
        // 从 sessionStorage 恢复
        const savedTotalAmount = sessionStorage.getItem('invoiceIssue_totalAmount')
        if (savedTotalAmount) {
          this.totalAmount = parseInt(savedTotalAmount) || 0
        }
      }
      
      if (newQuery.paymentIds) {
        this.paymentIds = newQuery.paymentIds.split(',').filter(id => id)
        sessionStorage.setItem('invoiceIssue_paymentIds', newQuery.paymentIds)
      } else {
        // 从 sessionStorage 恢复
        const savedPaymentIds = sessionStorage.getItem('invoiceIssue_paymentIds')
        if (savedPaymentIds) {
          this.paymentIds = savedPaymentIds.split(',').filter(id => id)
        }
      }
      
      // 恢复订单类型
      if (newQuery.orderType) {
        this.orderType = newQuery.orderType
        sessionStorage.setItem('invoiceIssue_orderType', newQuery.orderType)
      } else {
        const savedOrderType = sessionStorage.getItem('invoiceIssue_orderType')
        if (savedOrderType) {
          this.orderType = savedOrderType
        }
      }
    }
  },
  beforeUnmount () {
    // 组件卸载时清理 sessionStorage（可选，如果希望保留数据可以注释掉）
    // sessionStorage.removeItem('invoiceIssue_totalAmount')
    // sessionStorage.removeItem('invoiceIssue_paymentIds')
  }
}
</script>

<style scoped>
.invoice-issue-container {
  width: 100vw;
  max-width: 430px;
  min-height: 100vh;
  background: #f7f8fa;
  margin: 0 auto;
  box-sizing: border-box;
  padding-bottom: 80px;
}

.invoice-issue-header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
  position: relative;
  font-size: 20px;
  font-weight: 600;
}

.invoice-issue-header .back {
  position: absolute;
  left: 0;
  height: 56px;
  display: flex;
  align-items: center;
  font-size: 26px;
  padding-left: 16px;
  cursor: pointer;
}

.invoice-issue-card {
  background: #fff;
  border-radius: 16px;
  margin: 24px 16px 0 16px;
  padding: 18px 18px 18px 18px;
  box-shadow: 0 2px 8px #f0f0f0;
}

.invoice-issue-card-label {
  font-size: 15px;
  color: #888;
  margin-bottom: 10px;
}

.invoice-issue-card-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
}

.invoice-issue-card-row:first-of-type {
  margin-top: 0;
}

.invoice-issue-card-value {
  font-size: 17px;
  color: #222;
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-issue-card-link {
  font-size: 15px;
  color: #238aff;
  cursor: pointer;
  margin-left: 12px;
  flex-shrink: 0;
}

.invoice-issue-card-label-small {
  font-size: 15px;
  color: #888;
}

.invoice-issue-card-value-small {
  font-size: 16px;
  color: #222;
}

.invoice-issue-amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.invoice-issue-amount-label {
  font-size: 17px;
  color: #888;
}

.invoice-issue-amount-value {
  font-size: 24px;
  color: #222;
  font-weight: 600;
}

.invoice-issue-notice {
  font-size: 15px;
  color: #888;
  margin-bottom: 6px;
}

.invoice-issue-notice-link {
  font-size: 15px;
  color: #00baff;
  cursor: pointer;
  margin-top: 6px;
}

.invoice-issue-actions {
  margin: 38px 16px 0 16px;
}

.invoice-issue-btn {
  width: 100%;
  background: #238aff;
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  padding: 18px 0;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

.invoice-issue-btn:active {
  background: #1a6cd9;
  transform: scale(0.98);
}

.invoice-issue-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}
</style>
